import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scan, Search, AlertCircle, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { foodAPI } from '../services/api';

const FoodScan = () => {
  const [activeTab, setActiveTab] = useState('barcode');
  
  // Barcode State
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleBarcodeScan = async () => {
    if (!barcodeInput.trim()) return;
    
    setScanning(true);
    try {
      const result = await foodAPI.scanBarcode(barcodeInput);
      setScanResult(result);
      
      // Auto-focus on carbs after scan
      if (result.found) {
        setTimeout(() => {
          document.getElementById('carbs-info')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    } catch (error) {
      setScanResult({ found: false, error: 'Product not found' });
    }
    setScanning(false);
  };

  const handleFoodSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const result = await foodAPI.search(searchQuery);
      setSearchResults(result.products || []);
    } catch (error) {
      setSearchResults([]);
    }
    setSearching(false);
  };

  const selectProduct = async (barcode) => {
    setActiveTab('barcode');
    setBarcodeInput(barcode);
    setScanning(true);
    try {
      const result = await foodAPI.scanBarcode(barcode);
      setScanResult(result);
    } catch (error) {
      setScanResult({ found: false, error: 'Product not found' });
    }
    setScanning(false);
  };

  const getCarbsImpact = (carbs) => {
    if (carbs < 10) return { level: 'low', color: 'text-green-600', bg: 'bg-green-50', label: 'Low impact', icon: TrendingDown };
    if (carbs < 30) return { level: 'medium', color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Moderate impact', icon: Activity };
    return { level: 'high', color: 'text-red-600', bg: 'bg-red-50', label: 'High impact', icon: TrendingUp };
  };

  const getNutriScoreColor = (score) => {
    const colors = {
      'a': 'bg-green-600',
      'b': 'bg-lime-500',
      'c': 'bg-yellow-500',
      'd': 'bg-orange-500',
      'e': 'bg-red-600'
    };
    return colors[score?.toLowerCase()] || 'bg-gray-400';
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Scan className="w-9 h-9" />
          </div>
          <div>
            <h2 className="text-4xl font-bold">Food Scan</h2>
            <p className="text-purple-100 text-lg mt-1">Analyze carbs instantly</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <p className="text-sm text-purple-100">Database</p>
            <p className="text-2xl font-bold mt-1">2M+ products</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <p className="text-sm text-purple-100">Accuracy</p>
            <p className="text-2xl font-bold mt-1">98%</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <p className="text-sm text-purple-100">Instant Scan</p>
            <p className="text-2xl font-bold mt-1">&lt; 2s</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2 mx-auto">
          <TabsTrigger value="barcode">
            <Scan className="w-4 h-4 mr-2" />
            Scan
          </TabsTrigger>
          <TabsTrigger value="search">
            <Search className="w-4 h-4 mr-2" />
            Search
          </TabsTrigger>
        </TabsList>

        {/* Barcode Scan Tab */}
        <TabsContent value="barcode" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Scan a Barcode</CardTitle>
              <CardDescription>
                Enter product barcode to get nutritional information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="barcode">Barcode</Label>
                <div className="flex space-x-2">
                  <Input
                    id="barcode"
                    type="text"
                    placeholder="Example: 3017620422003"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleBarcodeScan()}
                    className="text-lg"
                  />
                  <Button
                    onClick={handleBarcodeScan}
                    disabled={scanning}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8"
                  >
                    {scanning ? 'Scanning...' : 'Scan'}
                  </Button>
                </div>
              </div>

              {/* Quick examples */}
              <div className="flex flex-wrap gap-2">
                <p className="text-sm text-gray-600 w-full">Examples:</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBarcodeInput('3017620422003'); handleBarcodeScan(); }}
                >
                  Nutella
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBarcodeInput('5449000000996'); handleBarcodeScan(); }}
                >
                  Coca-Cola
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setBarcodeInput('7613034626844'); handleBarcodeScan(); }}
                >
                  Chocolate
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scan Result */}
          {scanResult && scanResult.found && (
            <Card id="carbs-info" className="border-purple-200 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-6">
                  {scanResult.image && (
                    <img
                      src={scanResult.image}
                      alt={scanResult.name}
                      className="w-32 h-32 object-cover rounded-lg shadow-md"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900">{scanResult.name}</h3>
                    <p className="text-gray-600 mt-1">{scanResult.brand}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Serving: {scanResult.serving_size}
                    </p>
                    
                    {scanResult.nutriscore && (
                      <div className="flex items-center space-x-2 mt-3">
                        <span className="text-sm text-gray-600">Nutri-Score:</span>
                        <div className={`${getNutriScoreColor(scanResult.nutriscore)} text-white font-bold px-3 py-1 rounded-lg uppercase text-sm`}>
                          {scanResult.nutriscore}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Carbs Analysis - PROMINENT */}
                <div className="mt-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border-2 border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Carbs Analysis</h4>
                    {(() => {
                      const impact = getCarbsImpact(scanResult.nutrition.carbs);
                      const Icon = impact.icon;
                      return (
                        <Badge className={`${impact.bg} ${impact.color} px-4 py-2 text-base`}>
                          <Icon className="w-4 h-4 mr-2" />
                          {impact.label}
                        </Badge>
                      );
                    })()}
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-600 mb-1">Total Carbs</p>
                      <p className="text-4xl font-bold text-purple-600">
                        {scanResult.nutrition.carbs}g
                      </p>
                      <p className="text-xs text-gray-500 mt-2">per 100g</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-600 mb-1">Sugars</p>
                      <p className="text-4xl font-bold text-red-600">
                        {scanResult.nutrition.sugar}g
                      </p>
                      <p className="text-xs text-gray-500 mt-2">of which sugars</p>
                    </div>
                  </div>

                  {/* Carbs Progress Bar */}
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Glycemic impact</span>
                      <span className="font-semibold">{Math.min(100, (scanResult.nutrition.carbs / 50) * 100).toFixed(0)}%</span>
                    </div>
                    <Progress 
                      value={Math.min(100, (scanResult.nutrition.carbs / 50) * 100)} 
                      className="h-3"
                    />
                  </div>
                </div>

                {/* Nutrition Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Calories</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {scanResult.nutrition.calories}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">kcal</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Protein</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {scanResult.nutrition.protein}g
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Fats</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {scanResult.nutrition.fat}g
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Fiber</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {scanResult.nutrition.fiber}g
                    </p>
                  </div>
                </div>

                {/* Ingredients */}
                {scanResult.ingredients && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Ingredients</h4>
                    <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-lg">
                      {scanResult.ingredients}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {scanResult && !scanResult.found && (
            <Card className="border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3 text-red-600">
                  <AlertCircle className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">Product not found</p>
                    <p className="text-sm text-gray-600">
                      Barcode {barcodeInput} is not in our database
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Search for Food</CardTitle>
              <CardDescription>
                Quickly find nutritional information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="search">Product name</Label>
                <div className="flex space-x-2">
                  <Input
                    id="search"
                    type="text"
                    placeholder="Example: whole wheat bread"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleFoodSearch()}
                    className="text-lg"
                  />
                  <Button
                    onClick={handleFoodSearch}
                    disabled={searching}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8"
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Results ({searchResults.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {searchResults.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:shadow-md transition-all cursor-pointer"
                      onClick={() => selectProduct(product.barcode)}
                    >
                      <div className="flex items-center space-x-4">
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-600">{product.brand}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm">
                            <span className="text-purple-600 font-semibold">
                              {product.nutrition.carbs}g carbs
                            </span>
                            <span className="text-gray-500">
                              {product.nutrition.calories} cal
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View details
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FoodScan;
